import {combineLatest as observableCombineLatest, Observable} from 'rxjs';

import {catchError, first, map} from 'rxjs/operators';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  forwardRef,
  Inject,
  Input,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';
import {MatDialog, MatDialogRef} from '@angular/material/dialog';
import {MatSelect} from '@angular/material/select';
import {ActionBarConfiguration} from '../../../cache/uiconfiguration/actionbarconfiguration';
import {SmdDataTable} from './datatable.component';
import {SmdDataRowIcon, SmdDataRowModel} from './datatable-row.component';
import {CacheService} from '../../../cache/cache.service';
import {LanguageService} from '../../../language/language.service';
import {AddViewEvent} from '../../../resource/events/addviewevent';
import {SearchScopeConfiguration} from '../../../cache/uiconfiguration/searchscopeconfiguration';
import {ResourceService} from '../../../resource/resource.service';
import {FilterResult} from '../../../resource/filterresult';
import {LayoutService} from '../../../layout/layout.service';
import {animate, style, transition, trigger} from '@angular/animations';
import {RequestDialogComponent} from '../../../resource/request-approval/request-dialog.component';
import {FilterbuilderDialogComponent} from '../../filterbuilder/filterbuilder-dialog.component';
import {EnvironmentConfigService} from '../../../core/services/environment-config.service';
import {DomSanitizer, SafeUrl} from '@angular/platform-browser';
import {CsvExportDialogComponent} from '../../../resource/csv-export-dialog/csv-export-dialog.component';
import {SmdFilter} from '../smd-parts/smd-filter.component';

@Component({
  selector: 'smd-datatable-header',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './datatable-header.component.html',
  standalone: false,
})
export class SmdDatatableHeader {
  /** action bar site types that act on the selected rows (and therefore render the checkboxes) */
  public static readonly SELECTION_SITETYPES: string[] = [
    'wizard',
    'batch',
    'batchlist',
    'approval',
    'approvalbatch',
  ];

  languageResource: Observable<string>;

  public filterValue: string;

  objectUrl: SafeUrl;
  fileName: string;

  @Input() title: string = null;
  @ViewChild('scopeSelect') scopeSelect: MatSelect;
  @ViewChild(SmdFilter) filterComponent: SmdFilter;

  @Input() set initFilterValue(value: string) {
    if (value == null) return;
    this.filterValue = value;
    this._parent._onFilter();
  }

  enableEdit: boolean = false;
  enableDelete: boolean = false;
  enableFilter: boolean = false;
  enableRecent: boolean = false;
  enableCreate: boolean = false;
  enableApprove: boolean = false;
  enableReject: boolean = false;
  enableCSVExport: boolean = false;
  enableFilterbuilder: boolean = false;
  localSwitchLanguageKey: string = null;
  localSwitchLanguageDefaultValue: string = null;
  actionBarItems: ActionBarConfiguration[];
  @Input() listviewActionButtons: ListviewActionButton[] = null;
  @Input() showActionBarSpacer: boolean = true;

  @Output() onEdit: EventEmitter<any> = new EventEmitter<any>();
  @Output() onDelete: EventEmitter<any[]> = new EventEmitter<any[]>();
  @Output() onAction: EventEmitter<{
    conf: ActionBarConfiguration;
    selectedObjectIds?: string | string[];
  }> = new EventEmitter<{conf: ActionBarConfiguration; selectedObjectIds?: string | string[]}>();

  showScopeTooltip: Observable<string> = null;

  constructor(
    @Inject(forwardRef(() => SmdDataTable)) protected _parent: SmdDataTable,
    public changeDetector: ChangeDetectorRef,
    private cacheService: CacheService,
    private dialog: MatDialog,
    private resourceService: ResourceService,
    private layoutService: LayoutService,
    private sanitizer: DomSanitizer,
    private envConfig: EnvironmentConfigService,
  ) {
    this.languageResource = this.cacheService.getLanguageResource();
    this.showScopeTooltip = this.languageResource.pipe(
      map(lr =>
        LanguageService.translate('DatatableShowScopeTooltip', 'Choose a search scope', lr),
      ),
    );
  }

  public shouldRenderCheckbox() {
    return (
      this.enableEdit ||
      this.enableDelete ||
      this.enableApprove ||
      this.enableReject ||
      this.actionBarSelectionItems.length > 0
    );
  }

  _hasRowsSelected(): boolean {
    return this._parent.selectedRows().length > 0;
  }

  _selectedRowsLength(): number {
    return this._parent.selectedRows().length;
  }

  _onEditClick() {
    if (this._hasRowsSelected()) {
      let row: SmdDataRowModel = this._parent.selectedRows()[0];
      this.onEdit.emit(row.model);
      row.checked = false;
      this._parent._onRowCheckChange(row);
      this._parent.markAllRowsForCheck();
    }
  }

  get showHeader(): boolean {
    return (
      this.enableEdit ||
      this.enableDelete ||
      this.enableFilter ||
      this.enableRecent ||
      this.enableFilterbuilder ||
      this.enableApprove ||
      this.enableReject ||
      this.enableCSVExport ||
      (this.actionBarItems != null && this.actionBarItems.length > 0) ||
      (this.listviewActionButtons != null && this.listviewActionButtons.length > 0)
    );
  }

  _onDeleteClick() {
    if (this._hasRowsSelected()) {
      this.onDelete.emit(this._parent.selectedModels());
    }
  }

  _onCreateClick(): void {
    if (this._parent.selectedSearchScope != null) {
      this._parent.onAddView.emit(
        new AddViewEvent('create', this._parent.selectedSearchScope.resultobjecttype),
      );
    }
  }

  _onDownloadAsCSVClick() {
    let headers = this._parent.selectedSearchScope.attributedisplayed;
    if (typeof headers === 'string') {
      headers = headers.split(';');
    }
    let dialogRef: MatDialogRef<CsvExportDialogComponent> =
      this.dialog.open(CsvExportDialogComponent);
    dialogRef.componentInstance.init(
      headers,
      this._parent.filter,
      this._parent.header.filterValue,
      this._parent.selectedSearchScope.attributesearched,
      this._parent.attributes,
      this._parent.selectedSearchScope.resultobjecttype,
      this._parent.displayedTotalCount,
      this._parent.selectedSearchScope.sortingattribute,
      this._parent.selectedSearchScope.sortdirection,
      this._parent.selectedSearchScope.displayname + '.csv',
    );
  }

  _onApproveRejectClick() {
    const selectedIDs = this._parent
      .selectedRows()
      .map(rm => rm.model[this.envConfig.getAttributeName('ObjectID')]);

    let dialogRef: MatDialogRef<RequestDialogComponent> = this.dialog.open(RequestDialogComponent);
    dialogRef.componentInstance.init(selectedIDs);
    dialogRef.afterClosed().subscribe((val: any) => {
      this._parent.refreshData();
      // service needs a while to process data. refresh list multiple times.
      [100, 500, 1000, 1500, 3000].forEach((timeout: number) => {
        setTimeout(() => {
          this._parent.refreshData();
        }, timeout);
      });
    });
  }

  _onActionClick(conf: ActionBarConfiguration) {
    const selectedRows = this._parent.selectedRows();
    if (selectedRows.length === 0) {
      this.onAction.emit({conf: conf});
    } else if (selectedRows.length === 1) {
      this.onAction.emit({
        conf: conf,
        selectedObjectIds: selectedRows[0].model[this.envConfig.getAttributeName('ObjectID')],
      });
    } else {
      this.onAction.emit({
        conf: conf,
        selectedObjectIds: selectedRows.map(
          rm => rm.model[this.envConfig.getAttributeName('ObjectID')],
        ),
      });
    }
  }

  _onRecentClick(): void {
    this.enableFilter = false;
    this._parent.localColumns = [this.envConfig.getAttributeName('DisplayName')];
    this._parent.localResultObjectType = this.envConfig.getObjectTypeName('Resource');
    this._parent.localRowModels = [];
    this.layoutService.updatePreloaderState('active');
    const recentObs: Observable<FilterResult>[] = [];
    this.cacheService.getRecentGuids().forEach((guid: string) => {
      this._parent.searchScopeConfigs.forEach((ssc: SearchScopeConfiguration) => {
        let filter: string = ssc.xpath;
        if (filter != null) {
          const regex = /^([^[]+)\[(.+)\]$/;
          if (regex.test(filter)) {
            let matches: RegExpExecArray = regex.exec(filter);
            filter =
              matches[1] +
              '[(' +
              matches[2] +
              ') and (' +
              this.envConfig.getAttributeName('ObjectID') +
              "='" +
              guid +
              "')]";
          } else {
            filter =
              filter + '[' + this.envConfig.getAttributeName('ObjectID') + "='" + guid + "']";
          }
          recentObs.push(
            this.resourceService.searchResources(
              filter,
              [
                this.envConfig.getAttributeName('ObjectID'),
                this.envConfig.getAttributeName('DisplayName'),
              ].join(','),
              1,
            ),
          );
        }
      });
    });

    if (recentObs.length === 0) {
      this.layoutService.updatePreloaderState('hide');
      this._parent.ShowLocal = true;
    } else {
      observableCombineLatest(recentObs)
        .pipe(
          catchError((error, caught) => this._parent.handleError(error, caught)),
          first(),
        )
        .subscribe(
          (frs: FilterResult[]) => {
            let objects: string[] = [];
            frs.forEach((fr: FilterResult) => {
              if (fr != null && fr.TotalCount === 1) {
                if (objects.indexOf(fr.Results[0].ObjectID) == -1) {
                  objects.push(fr.Results[0].ObjectID);
                  this._parent.localRowModels.push(
                    new SmdDataRowModel(
                      fr.Results[0],
                      null,
                      null,
                      new SmdDataRowIcon('navigate_next'),
                    ),
                  );
                }
              }
            });
            this.layoutService.updatePreloaderState('hide');

            this._parent.ShowLocal = true;
          },
          () => {
            this.layoutService.updatePreloaderState('hide');
          },
        );
    }
  }

  _onSearchClick(): void {
    this.enableFilter = true;
    this._parent.ShowLocal = false;
  }

  get actionBarItemsLeft(): ActionBarConfiguration[] {
    if (this.actionBarItems == null) return [];
    return this.actionBarItems
      .filter(value => value.position != 'right')
      .sort((a, b) => a.order - b.order);
  }

  get actionBarItemsRight(): ActionBarConfiguration[] {
    if (this.actionBarItems == null) return [];
    return this.actionBarItems
      .filter(value => value.position == 'right')
      .sort((a, b) => a.order - b.order);
  }

  isActionBarItemDisabled(conf: ActionBarConfiguration): boolean {
    if (conf == null) return false;

    switch (conf.sitetype) {
      case 'wizard':
      case 'approval':
        return this._parent.selectedRowsLength() !== 1;
      case 'batch':
      case 'approvalbatch':
        return this._parent.selectedRowsLength() < 2;
      case 'batchlist':
        return this._parent.selectedRowsLength() < 1;
      default:
        return false;
    }
  }

  get actionBarSelectionItems(): ActionBarConfiguration[] {
    if (this.actionBarItems == null) return [];
    return this.actionBarItems
      .filter(value => SmdDatatableHeader.SELECTION_SITETYPES.indexOf(value.sitetype) > -1)
      .sort((a, b) => a.order - b.order)
      .sort((a, b) => (a.position === b.position ? 0 : a.position === 'right' ? 1 : -1));
  }

  isActionBarSelectionItemDisabled(conf: ActionBarConfiguration): boolean {
    if (conf == null) return false;
    const selectedRows: number = this._parent.selectedRowsLength();
    if (selectedRows === 0) return false;
    if (selectedRows === 1) {
      return ['wizard', 'approval', 'batchlist'].indexOf(conf.sitetype) === -1;
    } else {
      return ['batch', 'approvalbatch', 'batchlist'].indexOf(conf.sitetype) === -1;
    }
  }

  isActionBarBrotherInvisible(conf: ActionBarConfiguration): boolean {
    if (conf == null) return false;
    if (conf.hasbrother === false) return false;
    const selectedRows: number = this._parent.selectedRowsLength();
    // the bulk brother (batch / batchlist) is hidden while its wizard brother applies
    if (
      selectedRows <= 1 &&
      (conf.sitetype === 'batch' ||
        conf.sitetype === 'approvalbatch' ||
        conf.sitetype === 'batchlist')
    )
      return true;
    if (selectedRows > 1 && (conf.sitetype === 'wizard' || conf.sitetype === 'approval'))
      return true;
    return false;
  }

  @Input()
  set actionBarConfigs(confs: ActionBarConfiguration[]) {
    this.resetActionBar();
    if (confs == null) return;
    confs.forEach((conf: ActionBarConfiguration) => {
      if (conf != null) {
        if (conf.displayname === 'defaultFilter') {
          this.enableFilter = true;
        } else if (conf.displayname === 'defaultEdit') {
          this.enableEdit = true;
        } else if (conf.displayname === 'defaultDelete') {
          this.enableDelete = true;
        } else if (conf.displayname === 'defaultCreate') {
          this.enableCreate = true;
        } else if (conf.displayname === 'defaultApprove') {
          this.enableApprove = true;
        } else if (conf.displayname === 'defaultReject') {
          this.enableReject = true;
        } else if (conf.displayname === 'defaultCSVExport') {
          this.enableCSVExport = true;
        } else if (conf.displayname === 'defaultAdvancedFilter') {
          this.enableFilterbuilder = true;
        } else if (conf.displayname === ActionBarConfiguration.defaultRecentDisplayName) {
          this.enableFilter = true;
          this.enableRecent = true;
        } else {
          this.actionBarItems.push(conf);
        }
      }
    });
    this.changeDetector.markForCheck();
    this._parent.changeDetector.markForCheck();
  }

  resetActionBar(): void {
    this.enableRecent = false;
    this.enableFilter = false;
    this.enableEdit = false;
    this.enableDelete = false;
    this.enableCreate = false;
    this.enableApprove = false;
    this.enableReject = false;
    this.enableCSVExport = false;
    this.enableFilterbuilder = false;
    this.localSwitchLanguageKey = null;
    this.localSwitchLanguageDefaultValue = null;
    this.actionBarItems = [];
  }
}

export class ListviewActionButton {
  constructor(
    public icon: string,
    public tooltip: Observable<string>,
    public action: () => void,
  ) {}
}
